/**
 * OverlayShell — internal responsive overlay engine (ADR-114).
 *
 * Shared by DialogContent, AlertDialogContent, BottomSheet and Drawer.
 * Responsibilities:
 * - portal to document.body;
 * - centered dialog (>= SHEET_BREAKPOINT) vs bottom-sheet (mobile);
 * - header/body/footer compatible layout with max-height in dvh;
 * - scroll-lock registration, focus trap, ESC and backdrop dismissal;
 * - drag-to-dismiss for bottom-sheets.
 *
 * Not exported publicly; consume the concrete primitives instead.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  OVERLAY_MAX_HEIGHT_CLASS,
  OVERLAY_SIZE_CLASS,
  SHEET_MAX_HEIGHT_CLASS,
  type OverlaySize,
} from './constants';
import { useOverlayRegistration } from './OverlayProvider';
import { useFocusTrap } from './useFocusTrap';

export interface OverlayShellProps {
  open: boolean;
  onClose?: () => void;
  /** When false, ESC / backdrop / drag do not close the overlay. */
  dismissible?: boolean;
  mode?: 'dialog' | 'sheet';
  role?: 'dialog' | 'alertdialog';
  size?: OverlaySize;
  className?: string;
  children: ReactNode;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  /** Enables the drag handle in sheet mode. */
  dragDismiss?: boolean;
  showClose?: boolean;
  closeLabel?: string;
  testId?: string;
}

const DRAG_CLOSE_RATIO = 0.3;
const DRAG_CLOSE_MIN_PX = 80;

export function OverlayShell({
  open,
  onClose,
  dismissible = true,
  mode = 'dialog',
  role = 'dialog',
  size = 'lg',
  className,
  children,
  ariaLabel,
  ariaLabelledBy,
  dragDismiss = false,
  showClose = true,
  closeLabel = 'Close',
  testId = 'overlay-content',
}: OverlayShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startY: number; dragging: boolean }>({
    startY: 0,
    dragging: false,
  });
  const [dragY, setDragY] = useState(0);

  useOverlayRegistration(open);
  useFocusTrap(open, contentRef);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, dismissible, onClose]);

  const handleBackdropClick = useCallback(() => {
    if (dismissible) onClose?.();
  }, [dismissible, onClose]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragDismiss || !dismissible) return;
      dragState.current = { startY: event.clientY, dragging: true };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [dragDismiss, dismissible],
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current.dragging) return;
    setDragY(Math.max(0, event.clientY - dragState.current.startY));
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!dragState.current.dragging) return;
    dragState.current.dragging = false;
    const height = contentRef.current?.offsetHeight ?? 0;
    if (dragY > Math.max(DRAG_CLOSE_MIN_PX, height * DRAG_CLOSE_RATIO)) {
      onClose?.();
    }
    setDragY(0);
  }, [dragY, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const isSheet = mode === 'sheet';

  const content = (
    <div
      className={cn('fixed inset-0 z-50 flex', isSheet ? 'items-end' : 'items-center justify-center p-4')}
      data-overlay-mode={isSheet ? 'sheet' : 'dialog'}
    >
      <div
        className="fixed inset-0 bg-black/50"
        onClick={handleBackdropClick}
        aria-hidden="true"
        data-testid="overlay-backdrop"
      />
      <div
        ref={contentRef}
        role={role}
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        style={
          isSheet && dragY > 0
            ? {
                transform: `translateY(${dragY}px)`,
                transition: dragState.current.dragging ? 'none' : 'transform 150ms ease',
              }
            : undefined
        }
        className={cn(
          'relative z-10 flex w-full flex-col overflow-y-auto bg-white p-6 shadow-xl outline-none',
          'dark:bg-gray-900 dark:text-gray-100',
          isSheet
            ? cn('rounded-t-2xl', SHEET_MAX_HEIGHT_CLASS)
            : cn(
                'rounded-xl border border-gray-200 dark:border-gray-700',
                OVERLAY_MAX_HEIGHT_CLASS,
                'max-w-[calc(100vw-2rem)]',
                OVERLAY_SIZE_CLASS[size],
              ),
          className,
          isSheet && 'w-full max-w-none',
        )}
        data-overlay-mode={isSheet ? 'sheet' : 'dialog'}
        data-testid={testId}
      >
        {isSheet && dragDismiss && (
          <div
            className="mb-2 -mt-3 flex shrink-0 cursor-grab touch-none justify-center pb-1 pt-1"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            data-testid="overlay-drag-handle"
            aria-hidden="true"
          >
            <div className="h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>
        )}

        {children}

        {showClose && (
          <button
            type="button"
            onClick={() => onClose?.()}
            className="absolute right-4 top-4 rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={closeLabel}
            data-testid="overlay-close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
